/**
 * Decide WHEN to call the LLM and WHICH messages to send. We watch the conversation
 * for fact-relevant mentions (income, household, voucher) and, when any fires on a
 * lead message, keep that message plus a little surrounding context. Only the most
 * recent windows per detector are retained, so a changed value still reaches the LLM.
 * The detectors are an internal heuristic; the LLM extracts every field regardless.
 */

import { EMessageDirection } from 'src/services/api/messages/types';

import type { SpeakerMessage } from 'src/services/lead-facts/types';

/* How many lead messages a question stays "open" waiting for its answer. */
const PENDING_TTL = 4;
/* How many previous lead AND previous user messages to include in a context window. */
const CONTEXT_PREV = 1;
/* How many of the most recent trigger windows to keep per detector. */
const MAX_CONTEXT_WINDOWS = 2;

const VOUCHER_TERM =
    '(?:section\\s*-?\\s*8|sec\\s*8|housing\\s+(?:choice\\s+)?voucher|vouchers?|hcv|vash|ehv' +
    '|housing\\s+assistance|rental\\s+assistance)';

/* An income-shaped amount: $N, a 3+ digit number, a comma-grouped number, or "Nk". */
const INCOME_AMOUNT = /\$\s*\d|\b\d{3,}\b|\b\d{1,3}(?:,\d{3})+\b|\b\d+\s*k\b/i;

type FactDetector = {
    statement: RegExp;
    question: RegExp;
    gate?: RegExp;
};

/*
 * Fact-relevant message detectors. A lead `statement` (gated where noisy) or a recent
 * user `question` makes a message worth sending. These are detection heuristics only.
 */
const DETECTORS: FactDetector[] = [
    {
        statement:
            /\b(income|salar(?:y|ies)|earns?|earning|wages?|paycheck|gross|net\s+pay|take[\s-]?home|makes?|making|paid|ssi|ssdi|social\s+security|calworks|disability|pension|unemployment|child\s+support|cash\s+aid)\b|\$\s*\d|\b\d+\s*k\b|\b(?:a|per)\s+(?:month|year|week|hour)\b|\b(?:monthly|annual(?:ly)?|yearly|weekly)\b|\/(?:mo|yr|month|year)\b/i,
        question:
            /\b(income|earn|make|salary|how\s+much|before\s+taxes|gross|annual\s+income|monthly\s+income|household\s+income)\b/i,
        gate: INCOME_AMOUNT,
    },
    {
        statement:
            /\b(people|persons?|household|famil(?:y|ies)|adults?|kids?|child|children|sons?|daughters?|occupants?|tenants?|residents?|roommates?|bab(?:y|ies)|pregnant|expecting|just\s+me|by\s+myself|live\s+alone|of\s+us)\b|\bme\s+and\b|\bmyself\b/i,
        question:
            /\b(how\s+many|household|people\s+(?:will|in|living|are)|family\s+size|size\s+of\s+(?:your\s+)?household|occupants|in\s+the\s+(?:unit|home|apartment|household))\b/i,
    },
    {
        statement: new RegExp(`\\b${VOUCHER_TERM}\\b`, 'i'),
        question: new RegExp(`\\b${VOUCHER_TERM}\\b`, 'i'),
    },
];

function lastN(list: number[], next: number, n: number): number[] {
    return [...list, next].slice(-n);
}

/**
 * Stream the conversation and return the sorted, de-duplicated message indices worth
 * sending to the LLM as context. Empty when nothing fact-relevant came up.
 */
export function computeFactContext(messages: SpeakerMessage[]): number[] {
    const pending = new Map<number, number>(); /* detector index -> remaining TTL */
    let recentUser: number[] = [];
    let recentLead: number[] = [];
    /*
     * Per detector, the most recent trigger windows (oldest first); only the last
     * MAX_CONTEXT_WINDOWS are retained so the slice tracks the newest mentions.
     */
    const windows: number[][][] = DETECTORS.map(() => []);

    messages.forEach((msg, i) => {
        const text = msg.contents || '';

        if (msg.direction !== EMessageDirection.IN) {
            if (text.includes('?')) {
                DETECTORS.forEach((detector, di) => {
                    if (detector.question.test(text)) {
                        pending.set(di, PENDING_TTL);
                    }
                });
            }
            recentUser = lastN(recentUser, i, CONTEXT_PREV);
            return;
        }

        const fired = new Set<number>();
        DETECTORS.forEach((detector, di) => {
            if (detector.gate && !detector.gate.test(text)) {
                return;
            }
            if ((pending.get(di) ?? 0) > 0 || detector.statement.test(text)) {
                fired.add(di);
            }
        });

        if (fired.size > 0) {
            const ctx = [i, ...recentUser, ...recentLead];
            for (const di of fired) {
                windows[di].push(ctx);
                if (windows[di].length > MAX_CONTEXT_WINDOWS) {
                    windows[di].shift();
                }
            }
        }

        for (const di of [...pending.keys()]) {
            if (fired.has(di)) {
                pending.delete(di);
            } else {
                const remaining = (pending.get(di) ?? 0) - 1;
                if (remaining <= 0) {
                    pending.delete(di);
                } else {
                    pending.set(di, remaining);
                }
            }
        }
        recentLead = lastN(recentLead, i, CONTEXT_PREV);
    });

    const indices = new Set<number>();
    windows.forEach((perDetector) => perDetector.forEach((window) => window.forEach((idx) => indices.add(idx))));
    return [...indices].sort((a, b) => a - b);
}
